/**
 * Revenues memoized selectors
 */

import { createSelector } from '@reduxjs/toolkit';

import { selectColorMapping, selectCurrency, selectStores } from '@sonar/modules/Stores';

import {
  getSelectedPeriod,
  getSelectedStoresIds,
  getSelectedTimeAggregate,
  selectIsTaxIncluded,
} from '../../Preferences/selectors';
import { createChartData } from '../helpers';

/**
 * Get data about revenues retrieved from api
 *
 * @return {Object}
 */
export const getRevenuesEntities = ({ entities }) => entities.revenues.entities;

/**
 * Get status about revenues request
 * `none`, `loading`, `loaded` or `failed`
 *
 * @return {string}
 */
export const getRevenuesStatus = ({ entities }) => entities.revenues.status;

/**
 * If last attempt at fetching data resulted in an error, returns the error value
 *
 * @return {string}
 */
export const getRevenuesError = ({ entities }) => entities.revenues.error;

/**
 * If data is being fetched
 *
 * @return {boolean}
 */
export const areRevenuesLoading = createSelector(
  [getRevenuesStatus],
  status => status === 'loading',
);

/**
 *
 * Get revenues data of selected stores
 *
 * @return {Object}
 */
export const getSelectedRevenuesChart = createSelector(
  [getRevenuesEntities, getSelectedStoresIds],
  (revenuesEntities, selectedStoreIds) => {
    if (!revenuesEntities || !selectedStoreIds || !selectedStoreIds.length) {
      return null;
    }
    const revenues = Object.keys(revenuesEntities).reduce(
      (dataOfSelectedStores, storeId) =>
        selectedStoreIds.includes(parseInt(storeId, 10))
          ? {
              ...dataOfSelectedStores,
              ...(revenuesEntities[storeId] ? { [storeId]: revenuesEntities[storeId] } : {}),
            }
          : dataOfSelectedStores,
      {},
    );
    if (!Object.keys(revenues).length) {
      return null;
    }

    return revenues;
  },
);

/**
 * Get revenues data chart without tax
 * Calculates revenues without tax for bar chart on selected stores
 *
 * @return {Array}
 */
export const getRevenuesChartTaxExcluded = createSelector(
  [
    getSelectedPeriod,
    getSelectedTimeAggregate,
    getSelectedRevenuesChart,
    selectCurrency,
    selectStores,
    selectColorMapping,
  ],
  (period, aggregate, revenues, currency, stores, colorMapping) =>
    revenues
      ? createChartData({
          data: revenues,
          interval: period.interval,
          tax: 'tax_excluded',
          aggregate,
          colorMapping,
          currency,
          stores,
        })
      : [],
);

/**
 * Get revenues data chart with tax
 * Calculates revenues with tax for bar chart on selected stores
 *
 * @return {Array}
 */
export const getRevenuesChartTaxIncluded = createSelector(
  [
    getSelectedPeriod,
    getSelectedTimeAggregate,
    getSelectedRevenuesChart,
    selectCurrency,
    selectStores,
    selectColorMapping,
  ],
  (period, aggregate, revenues, currency, stores, colorMapping) =>
    revenues
      ? createChartData({
          data: revenues,
          interval: period.interval,
          tax: 'tax_included',
          aggregate,
          colorMapping,
          currency,
          stores,
        })
      : [],
);

/**
 * Calculates revenues for bar chart on selected stores
 *
 * @return {Array}
 */
export const getRevenuesChartData = createSelector(
  [selectIsTaxIncluded, getRevenuesChartTaxIncluded, getRevenuesChartTaxExcluded],
  (isTaxIncluded, revenuesTaxIncluded, revenuesTaxExcluded) =>
    isTaxIncluded ? revenuesTaxIncluded : revenuesTaxExcluded,
);
